import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSpinnerOverlayComponent } from 'src/app/components/mat-spinner-overlay/mat-spinner-overlay.component';
import * as XLSX from 'xlsx';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { AdmRedePostosServices } from '../adm-rede-postos.service';
import { LogMasterPosto } from '../macro-posto.interface';

@Component({
  selector: 'app-tabela-logs',
  templateUrl: './tabela-logs.component.html',
  styleUrls: ['./tabela-logs.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatDialogModule,
    MatSortModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatIconModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatSpinnerOverlayComponent,
  ],
})
export class TabelaLogsComponent implements OnInit {
  displayedColumns: string[] = [
    // 'id',
    'tipoAcao',
    'master',
    'usuario',
    'usuarioAfetado',
    'acao',
    'dataHora',
    'detalhes',
  ];
  dataSource = new MatTableDataSource<LogMasterPosto>([]);

  filterForm: FormGroup = new FormGroup({
    master: new FormControl(''),
    usuario: new FormControl(''),
    usuarioAfetado: new FormControl(''),
    dataHoraIncicio: new FormControl(''),
    dataHoraFim: new FormControl(''),
  });

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  public loading: boolean = false;
  private snack = inject(SnackBarService);

  constructor(private service: AdmRedePostosServices) {}

  ngOnInit(): void {
    this.loading = true;
    this.service.pegarListaLogs().subscribe(
      (ret: LogMasterPosto[]) => {
        this.dataSource.data = ret;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.snack.flashMessage(
          'Erro na requisição ao carregar os logs',
          ['snackbar-error'],
          'Fechar',
          3000
        );
      },
      () => (this.loading = false)
    );
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = (data, filter) => {
      const filters = JSON.parse(filter);

      const matchMaster =
        !filters.master ||
        data.master?.toLowerCase().includes(filters.master.toLowerCase());

      const matchUsuario =
        !filters.usuario ||
        data.usuario?.toLowerCase().includes(filters.usuario.toLowerCase());

      const matchUsuarioAfetado =
        !filters.usuarioAfetado ||
        data.usuarioAfetado
          ?.toLowerCase()
          .includes(filters.usuarioAfetado?.toLowerCase());

      // Converter datas do DatePicker Material para Date objects
      let dataHoraInicio: Date | null = null;
      let dataHoraFim: Date | null = null;

      if (filters.dataHoraIncicio) {
        const dataInicio = new Date(filters.dataHoraIncicio);
        if (!isNaN(dataInicio.getTime())) {
          dataHoraInicio = new Date(
            dataInicio.getFullYear(),
            dataInicio.getMonth(),
            dataInicio.getDate(),
            0,
            0,
            0
          );
        }
      }

      if (filters.dataHoraFim) {
        const dataFim = new Date(filters.dataHoraFim);
        if (!isNaN(dataFim.getTime())) {
          dataHoraFim = new Date(
            dataFim.getFullYear(),
            dataFim.getMonth(),
            dataFim.getDate(),
            23,
            59,
            59
          );
        }
      }

      // data.dataHora já é um Date object
      let dataHoraLog: Date | null = null;
      if (data.dataHora) {
        // Se for string, converter para Date; se for Date, usar direto
        if (typeof data.dataHora === 'string') {
          dataHoraLog = new Date(data.dataHora);
        } else if (data.dataHora instanceof Date) {
          dataHoraLog = new Date(data.dataHora);
        }

        // Validar se a data foi criada corretamente
        if (dataHoraLog && isNaN(dataHoraLog.getTime())) {
          dataHoraLog = null;
        }
      }

      // Aplicar filtro de data
      let matchDataHora = true;

      if (dataHoraLog) {
        if (dataHoraInicio && dataHoraFim) {
          matchDataHora =
            dataHoraLog >= dataHoraInicio && dataHoraLog <= dataHoraFim;
        } else if (dataHoraInicio) {
          matchDataHora = dataHoraLog >= dataHoraInicio;
        } else if (dataHoraFim) {
          matchDataHora = dataHoraLog <= dataHoraFim;
        }
      }

      return (
        matchMaster && matchUsuario && matchUsuarioAfetado && matchDataHora
      );
    };
  }

  buscar(): void {
    const filterValues: any = { ...this.filterForm.value };

    // Convert Date objects to ISO strings for serialization
    if (filterValues.dataHoraIncicio instanceof Date) {
      filterValues.dataHoraIncicio = filterValues.dataHoraIncicio.toISOString();
    } else if (filterValues.dataHoraIncicio) {
      filterValues.dataHoraIncicio = filterValues.dataHoraIncicio;
    }
    if (filterValues.dataHoraFim instanceof Date) {
      filterValues.dataHoraFim = filterValues.dataHoraFim.toISOString();
    } else if (filterValues.dataHoraFim) {
      filterValues.dataHoraFim = filterValues.dataHoraFim;
    }

    this.dataSource.filter = JSON.stringify(filterValues);
    this.dataSource.paginator?.firstPage();
  }

  clearFilter(): void {
    this.filterForm.reset();
    this.buscar();
  }

  getTipoIcon(tipoAcao: number): string {
    switch (tipoAcao) {
      case 1:
        return 'add_circle';
      case 2:
        return 'edit';
      case 3:
        return 'delete';
      default:
        return 'help_outline';
    }
  }
  getRowClass(tipoAcao: number): string {
    switch (tipoAcao) {
      case 1:
        return 'acao-criacao';
      case 2:
        return 'acao-edicao';
      case 3:
        return 'acao-exclusao';
      default:
        return '';
    }
  }

  getDadosExcel() {
    const ret = this.dataSource.filteredData.map((item) => {
      // Formatar data para string legível
      const dataHoraFormatada = item.dataHora
        ? new Date(item.dataHora).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '';

      return {
        'Tipo de Ação':
          item.tipoAcao === 1
            ? 'Criação'
            : item.tipoAcao === 2
            ? 'Edição'
            : 'Exclusão',
        'Macro de Posto': item.master,
        Usuário: item.usuario,
        'Usuário Afetado': item.usuarioAfetado,
        Ação: item.acao,
        'Data/Hora': dataHoraFormatada,
        Detalhes: item.detalhes,
      };
    });
    return ret;
  }

  exportToExcel(): void {
    const workSheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      this.getDadosExcel()
    );
    const workBook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workBook,
      workSheet,
      this.service.gerarNomeArquivo('Logs_Macros_Postos_')
    );
    XLSX.writeFile(
      workBook,
      `${this.service.gerarNomeArquivo('Logs_Macros_Postos_')}.xlsx`
    );
  }
}
